import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";
import Loader from "components/Loader/loader";
import ImageCarousel from "components/ImageCarousel";

import vehicleApprovalTableData from "layouts/vehicleApprovals/data/vehicleApprovalTableData";
import { getListings, getCategories } from "services/apiService";
import { approveListing } from "services/apiService";
import { rejectListing } from "services/apiService";
import { removeListing } from "services/apiService";
import { toggleListingVerified } from "services/apiService";
import { toggleListingDvlaInspected } from "services/apiService";
import resolveImageUrl from "utils/resolveAssetUrl";
import { LISTING_STATUS_FILTER_OPTIONS } from "utils/listingStatus";

const STATUS_OPTIONS = LISTING_STATUS_FILTER_OPTIONS;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest", icon: "arrow_downward" },
  { value: "oldest", label: "Oldest", icon: "arrow_upward" },
  { value: "price_high_low", label: "Price: High to Low", icon: "trending_down" },
  { value: "price_low_high", label: "Price: Low to High", icon: "trending_up" },
  { value: "category", label: "Category", icon: "category" },
];

// The global theme hides the native select arrow, so re-enable it per filter field.
const FILTER_SELECT_PROPS = {
  IconComponent: ArrowDropDownIcon,
  sx: {
    "& .MuiSelect-icon": { display: "block", right: 8, color: "text.secondary" },
    "& .MuiSelect-select": { paddingRight: "32px !important" },
  },
};

const optionContent = (icon, label, color) => (
  <MDBox display="flex" alignItems="center" gap={1}>
    <Icon fontSize="small" sx={{ color: color ? `${color}.main` : "text.secondary" }}>
      {icon}
    </Icon>
    <MDTypography variant="button" fontWeight="regular">
      {label}
    </MDTypography>
  </MDBox>
);

const mapListingToVehicle = (listing) => ({
  id: listing?.id,
  title: listing?.title,
  description: listing?.description,
  category: listing?.category?.name,
  condition: listing?.condition,
  location: listing?.location,
  kmDriven: listing?.km_driven,
  ownershipType: listing?.ownership_type,
  make: listing?.make,
  model: listing?.model,
  year: listing?.year,
  mileage: listing?.mileage,
  fuelType: listing?.fuelType,
  transmission: listing?.transmission,
  engine: listing?.engine,
  color: listing?.color,
  bodyType: listing?.bodyType,
  drivetrain: listing?.drivetrain,
  price: listing?.price,
  priceType: listing?.priceType,
  status: listing?.status,
  rejectionReason: listing?.rejectionReason,
  isVerified: listing?.isVerified ?? listing?.is_verified ?? false,
  isDvlaInspected:
    listing?.isDvlaInspected ?? listing?.is_dvla_inspected ?? listing?.dvlaInspected ?? false,
  photos: [...(listing?.images || [])]
    .sort((a, b) => (b?.isPrimary ? 1 : 0) - (a?.isPrimary ? 1 : 0) || a?.sortOrder - b?.sortOrder)
    .map((img) => resolveImageUrl(img?.imageUrl)),
  seller: listing?.seller?.fullName,
  sellerPhone: listing?.seller?.phone,
  submittedAt: listing?.createdAt
    ? new Date(listing.createdAt).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : undefined,
  updatedAt: listing?.updatedAt
    ? new Date(listing.updatedAt).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : undefined,
});

function VehicleApprovals() {
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [sortBy, setSortBy] = useState("newest");
  const [categoryId, setCategoryId] = useState(searchParams.get("categoryId") || "");
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");

  const [alert, setAlert] = useState({ open: false, type: "success", title: "", message: "" });
  const [viewOpen, setViewOpen] = useState(false);
  const [viewVehicle, setViewVehicle] = useState(null);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const response = await getListings({
        status: status || undefined,
        search: search || undefined,
        sortBy: sortBy || undefined,
        categoryId: categoryId || undefined,
        page,
        limit: pageCount,
      });
      if (response?.success) {
        setVehicles((response?.data?.listings || []).map(mapListingToVehicle));
        setTotalVehicles(response?.data?.total ?? 0);
        setTotalPages(response?.data?.totalPages ?? 1);
      }
    } catch (err) {
      console.error("Fetch listings failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageCount, status, sortBy, categoryId]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getCategories();
        if (response?.success) {
          const list = response?.data?.categories || response?.data || [];
          setCategories(list);
          // The categoryId arriving from the dashboard link is always a string; align it
          // with the option's own id type so the Select renders the matching label.
          setCategoryId((current) => {
            if (!current) return current;
            const match = list.find((cat) => String(cat.id) === String(current));
            return match ? match.id : current;
          });
        }
      } catch (err) {
        console.error("Fetch categories failed:", err);
      }
    })();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchListings();
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleView = (vehicle) => {
    setViewVehicle(vehicle);
    setViewOpen(true);
  };

  const handleApprove = async (id) => {
    setLoading(true);
    try {
      const res = await approveListing(id);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Failed to approve listing.",
        });
      } else {
        setAlert({
          open: true,
          type: "success",
          title: "Approved",
          message: res?.message || "Vehicle approved. It will now be visible to customers.",
        });
        fetchListings();
      }
    } catch (err) {
      console.error("Approve listing failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to approve listing.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = (id) => {
    setPendingId(id);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      setAlert({
        open: true,
        type: "error",
        title: "Validation Error",
        message: "Please provide a reason for rejection.",
      });
      return;
    }
    setLoading(true);
    try {
      const res = await rejectListing(pendingId, rejectReason.trim());
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Failed to reject listing.",
        });
      } else {
        setRejectDialogOpen(false);
        setPendingId(null);
        setAlert({
          open: true,
          type: "success",
          title: "Rejected",
          message: res?.message || "Vehicle listing rejected.",
        });
        fetchListings();
      }
    } catch (err) {
      console.error("Reject listing failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to reject listing.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = (id) => {
    setPendingRemoveId(id);
    setAlert({
      open: true,
      type: "confirm",
      title: "Remove Listing",
      message: "Are you sure you want to remove this listing? This cannot be undone.",
    });
  };

  const handleRemoveConfirm = async () => {
    setAlert((prev) => ({ ...prev, open: false }));
    setLoading(true);
    try {
      const res = await removeListing(pendingRemoveId);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Failed to remove listing.",
        });
      } else {
        setAlert({
          open: true,
          type: "success",
          title: "Removed",
          message: res?.message || "Vehicle listing removed.",
        });
        fetchListings();
      }
    } catch (err) {
      console.error("Remove listing failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to remove listing.",
      });
    } finally {
      setPendingRemoveId(null);
      setLoading(false);
    }
  };

  const handleToggleVerified = async () => {
    if (!viewVehicle?.id) return;
    setLoading(true);
    try {
      const res = await toggleListingVerified(viewVehicle.id);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Failed to update verified status.",
        });
      } else {
        setViewVehicle((prev) => (prev ? { ...prev, isVerified: !prev.isVerified } : prev));
        setAlert({
          open: true,
          type: "success",
          title: "Updated",
          message: res?.message || "Verified status updated.",
        });
        fetchListings();
      }
    } catch (err) {
      console.error("Toggle verified failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to update verified status.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDvlaInspected = async () => {
    if (!viewVehicle?.id) return;
    setLoading(true);
    try {
      const res = await toggleListingDvlaInspected(viewVehicle.id);
      if (res?.error) {
        setAlert({
          open: true,
          type: "error",
          title: "Oops!",
          message: res?.message || "Failed to update DVLA inspected status.",
        });
      } else {
        setViewVehicle((prev) =>
          prev ? { ...prev, isDvlaInspected: !prev.isDvlaInspected } : prev
        );
        setAlert({
          open: true,
          type: "success",
          title: "Updated",
          message: res?.message || "DVLA inspected status updated.",
        });
        fetchListings();
      }
    } catch (err) {
      console.error("Toggle DVLA inspected failed:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to update DVLA inspected status.",
      });
    } finally {
      setLoading(false);
    }
  };

  const { columns, rows } = useMemo(
    () =>
      vehicleApprovalTableData({
        vehicleList: vehicles,
        onView: handleView,
        onApprove: handleApprove,
        onReject: handleReject,
        onRemove: handleRemove,
      }),
    [vehicles]
  );
  console.log("Vehicles:", vehicles);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && <Loader />}

      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={handleRemoveConfirm}
      />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Vehicle Approvals
                </MDTypography>
              </MDBox>

              <MDBox px={2} pt={3} display="flex" flexWrap="wrap" gap={2}>
                <MDBox width="16rem">
                  <TextField
                    select
                    label="Status"
                    fullWidth
                    size="medium"
                    value={status}
                    onChange={(e) => updateFilter(setStatus)(e.target.value)}
                    SelectProps={FILTER_SELECT_PROPS}
                    sx={{ "& .MuiInputBase-root": { height: 44 } }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {optionContent(opt.icon, opt.label, opt.color)}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>

                <MDBox width="16rem">
                  <TextField
                    select
                    label="Category"
                    fullWidth
                    size="medium"
                    value={categoryId}
                    onChange={(e) => updateFilter(setCategoryId)(e.target.value)}
                    SelectProps={FILTER_SELECT_PROPS}
                    sx={{ "& .MuiInputBase-root": { height: 44 } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>

                <MDBox width="16rem">
                  <TextField
                    select
                    label="Sort By"
                    fullWidth
                    size="medium"
                    value={sortBy}
                    onChange={(e) => updateFilter(setSortBy)(e.target.value)}
                    SelectProps={FILTER_SELECT_PROPS}
                    sx={{ "& .MuiInputBase-root": { height: 44 } }}
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {optionContent(opt.icon, opt.label)}
                      </MenuItem>
                    ))}
                  </TextField>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 15, 20, 35, 50] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchValue={search}
                  onSearchChange={setSearch}
                  page={page}
                  onPageChange={setPage}
                  pageEntryCount={updateFilter(setPageCount)}
                  manualPagination
                  pageCount={totalPages}
                  totalEntries={totalVehicles}
                  onRowClick={(row) => row?.rowData && handleView(row.rowData)}
                />
              </MDBox>
            </Card>

            {/* ── View Vehicle Dialog ── */}
            <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>
                Vehicle Details
                <IconButton
                  aria-label="close"
                  onClick={() => setViewOpen(false)}
                  sx={{ position: "absolute", right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {/* Photos */}
                {viewVehicle?.photos?.length > 0 && (
                  <ImageCarousel
                    images={viewVehicle.photos}
                    alt={viewVehicle?.title}
                    height={260}
                  />
                )}

                <MDTypography variant="h5" fontWeight="bold">
                  {viewVehicle?.title}
                </MDTypography>

                <MDBox
                  mt={1}
                  mb={1}
                  px={1}
                  py={0.5}
                  display="inline-block"
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                >
                  <MDTypography variant="caption" color="text">
                    Submitted by {viewVehicle?.seller} ({viewVehicle?.sellerPhone}) on{" "}
                    {viewVehicle?.submittedAt}
                  </MDTypography>
                </MDBox>

                {viewVehicle?.status === "rejected" && (
                  <MDBox
                    mt={1}
                    p={1.5}
                    display="flex"
                    gap={1}
                    sx={{
                      border: "1px solid",
                      borderColor: "error.main",
                      borderRadius: 1,
                      backgroundColor: "rgba(244, 67, 53, 0.08)",
                    }}
                  >
                    <Icon fontSize="small" sx={{ color: "error.main", mt: 0.25 }}>
                      cancel
                    </Icon>
                    <MDBox>
                      <MDTypography variant="button" fontWeight="bold" color="error" display="block">
                        Rejected
                      </MDTypography>
                      <MDTypography variant="caption" color="dark" fontWeight="medium">
                        {viewVehicle?.rejectionReason || "No reason was recorded."}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Admin Toggles */}
                <MDBox
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                  mb={1}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!viewVehicle?.isVerified}
                        onChange={handleToggleVerified}
                      />
                    }
                    label={
                      <MDTypography variant="button" fontWeight="medium">
                        Document Verified
                      </MDTypography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!viewVehicle?.isDvlaInspected}
                        onChange={handleToggleDvlaInspected}
                      />
                    }
                    label={
                      <MDTypography variant="button" fontWeight="medium">
                        DVLA Inspected
                      </MDTypography>
                    }
                  />
                </MDBox>

                <Divider sx={{ my: 2 }} />

                {/* Basic Info */}
                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Basic Info
                </MDTypography>
                {[
                  { label: "Title", value: viewVehicle?.title },
                  { label: "Description", value: viewVehicle?.description },
                  { label: "Category", value: viewVehicle?.category },
                  { label: "Condition", value: viewVehicle?.condition },
                  { label: "Location", value: viewVehicle?.location },
                  { label: "KM Driven", value: viewVehicle?.kmDriven },
                  { label: "Ownership Type", value: viewVehicle?.ownershipType },
                ].map((row) => (
                  <MDBox
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                    py={0.75}
                    sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <MDTypography variant="caption" color="text" sx={{ flexShrink: 0 }}>
                      {row.label}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium" sx={{ textAlign: "right" }}>
                      {row.value || "—"}
                    </MDTypography>
                  </MDBox>
                ))}

                <Divider sx={{ my: 2 }} />

                {/* Vehicle Specifications */}
                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Vehicle Specifications
                </MDTypography>
                {[
                  { label: "Make", value: viewVehicle?.make },
                  { label: "Model", value: viewVehicle?.model },
                  { label: "Year", value: viewVehicle?.year },
                  { label: "Mileage", value: viewVehicle?.mileage },
                  { label: "Fuel Type", value: viewVehicle?.fuelType },
                  { label: "Transmission", value: viewVehicle?.transmission },
                  { label: "Engine", value: viewVehicle?.engine },
                  { label: "Color", value: viewVehicle?.color },
                  { label: "Body Type", value: viewVehicle?.bodyType },
                  { label: "Drivetrain", value: viewVehicle?.drivetrain },
                ].map((row) => (
                  <MDBox
                    key={row.label}
                    display="flex"
                    justifyContent="space-between"
                    gap={2}
                    py={0.75}
                    sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <MDTypography variant="caption" color="text" sx={{ flexShrink: 0 }}>
                      {row.label}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium" sx={{ textAlign: "right" }}>
                      {row.value || "—"}
                    </MDTypography>
                  </MDBox>
                ))}

                <Divider sx={{ my: 2 }} />

                {/* Pricing */}
                <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Pricing
                </MDTypography>
                <MDBox display="flex" justifyContent="space-between" gap={2} py={0.75}>
                  <MDTypography variant="caption" color="text">
                    Price
                  </MDTypography>
                  <MDTypography variant="h6" color="success" fontWeight="bold">
                    {viewVehicle?.price}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" gap={2} py={0.75}>
                  <MDTypography variant="caption" color="text">
                    Price Type
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    {viewVehicle?.priceType}
                  </MDTypography>
                </MDBox>
              </DialogContent>
            </Dialog>

            {/* ── Reject Vehicle Dialog ── */}
            <Dialog
              open={rejectDialogOpen}
              onClose={() => setRejectDialogOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Reject Vehicle</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  margin="dense"
                  label="Reason for rejection"
                  placeholder="Enter the reason for rejecting this listing..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRejectSubmit}
                  sx={{ color: "beige" }}
                >
                  Save
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>
      </MDBox>

    </DashboardLayout>
  );
}

export default VehicleApprovals;
